import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';

interface ClientTheme {
  name: string;
  themeClass: string;
  primaryColor: string;
}

interface ClientThemeContextType {
  theme: ClientTheme | null;
  clientName: string;
}

const ClientThemeContext = createContext<ClientThemeContextType>({
  theme: null,
  clientName: '',
});

export const useClientTheme = () => {
  const context = useContext(ClientThemeContext);
  if (!context) {
    throw new Error('useClientTheme must be used within ClientThemeProvider');
  }
  return context;
};

const clientThemeMap: Record<string, ClientTheme> = {
  'af4e3b3d-a155-456a-8763-54780f22ccf7': {
    name: 'St Austin',
    themeClass: 'client-austin-theme',
    primaryColor: 'amber',
  },
  '86a999a8-9952-428d-a273-83efcb6330fd': {
    name: 'Victoria Plasencia',
    themeClass: 'client-victoria-theme',
    primaryColor: 'blue',
  },
};

interface ClientThemeProviderProps {
  children: React.ReactNode;
}

export const ClientThemeProvider: React.FC<ClientThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const theme = useMemo(() => {
    if (!user?.client_project_id) return null;

    // Check sessionStorage first
    const cachedTheme = sessionStorage.getItem('client-theme');
    if (cachedTheme) {
      try {
        const parsed = JSON.parse(cachedTheme);
        if (parsed.client_project_id === user.client_project_id) {
          return parsed.theme;
        }
      } catch {
        // Invalid cache, will recalculate
      }
    }

    // Get theme from map
    const mappedTheme = clientThemeMap[user.client_project_id];
    if (mappedTheme) {
      // Cache the theme
      sessionStorage.setItem(
        'client-theme',
        JSON.stringify({
          client_project_id: user.client_project_id,
          theme: mappedTheme,
        })
      );
      return mappedTheme;
    }

    return null;
  }, [user?.client_project_id]);

  const clientName = useMemo(() => {
    if (!user?.name) return '';
    return user.name;
  }, [user?.name]);

  useEffect(() => {
    // Apply theme class to wrapper div
    if (theme?.themeClass) {
      const existingThemes = ['client-austin-theme', 'client-victoria-theme'];
      existingThemes.forEach((cls) => {
        document.body.classList.remove(cls);
      });
      document.body.classList.add(theme.themeClass);
    }

    return () => {
      // Cleanup on unmount
      const existingThemes = ['client-austin-theme', 'client-victoria-theme'];
      existingThemes.forEach((cls) => {
        document.body.classList.remove(cls);
      });
    };
  }, [theme?.themeClass]);

  // Clear theme from sessionStorage on logout
  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem('client-theme');
    }
  }, [user]);

  return (
    <ClientThemeContext.Provider value={{ theme, clientName }}>
      {children}
    </ClientThemeContext.Provider>
  );
};